import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  isStringNode,
  JsonError,
  JsonSchema,
  Node,
  StringNode,
} from '@sagold/react-json-editor';

import { isDateValid } from '../../components/datepicker/helper/isDateValid';
import {
  applyFormatters,
  dateFormatters,
  FormatterOptions,
  numberFormatters,
} from '../../components/table/valueFormatters';
import { getNodeFromJsonPath } from '../plugins/pluginHelper';

export function isNullableString(type: JsonSchema['type']) {
  return (
    type === 'string' ||
    (Array.isArray(type) &&
      type.includes('string') &&
      type.every((t) => t === 'string' || t === 'null'))
  );
}

export function isNullableNumber(type: JsonSchema['type']) {
  return (
    type === 'number' ||
    (Array.isArray(type) &&
      type.includes('number') &&
      type.every((t) => t === 'number' || t === 'null'))
  );
}

export function isNullableInteger(type: JsonSchema['type']) {
  return (
    type === 'integer' ||
    (Array.isArray(type) &&
      type.includes('integer') &&
      type.every((t) => t === 'integer' || t === 'null'))
  );
}

export function resolvePlaceholder(
  override: string | undefined,
  defaultPlaceholder: string
) {
  return override ?? defaultPlaceholder;
}

export function formatNumberValidationBound(
  value: number,
  options: FormatterOptions
) {
  return String(
    applyFormatters(numberFormatters, value, {
      ...options,
      // Form values and schema bounds are already converted to display space.
      factor: undefined,
    }) ?? ''
  );
}

export function formatDateValidationBound(
  value: unknown,
  options: FormatterOptions
) {
  const localValue =
    typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)
      ? `${value}T00:00:00`
      : value;

  return String(applyFormatters(dateFormatters, localValue, options) ?? '');
}

export function useErrorTransformation(errors: JsonError[]) {
  const { t } = useTranslation();
  return useMemo(() => {
    return errors.map((e) => {
      if (
        e.code === 'min-length-one-error' ||
        (e.code === 'type-error' && e.data['received'] === 'null')
      ) {
        return {
          ...e,
          message: t('app.forms.validationErrors.inputIsRequired'),
        };
      }
      // json-schema-library reuses maximum-error / minimum-error for exclusive
      // bounds and puts exclusiveMaximum / exclusiveMinimum into data.maximum /
      // data.minimum and then shows an incorrect error message.
      // Rewrite those so the UI shows exclusive wording.
      if (
        e.code === 'maximum-error' &&
        typeof e.data.schema?.exclusiveMaximum === 'number' &&
        e.data.maximum === e.data.schema.exclusiveMaximum
      ) {
        return {
          ...e,
          message: t('app.forms.validationErrors.exclusiveMaximumError', {
            expected: formatNumberValidationBound(
              e.data.schema.exclusiveMaximum,
              e.data.schema.options ?? {}
            ),
          }),
        };
      }
      if (
        e.code === 'minimum-error' &&
        typeof e.data.schema?.exclusiveMinimum === 'number' &&
        e.data.minimum === e.data.schema.exclusiveMinimum
      ) {
        return {
          ...e,
          message: t('app.forms.validationErrors.exclusiveMinimumError', {
            expected: formatNumberValidationBound(
              e.data.schema.exclusiveMinimum,
              e.data.schema.options ?? {}
            ),
          }),
        };
      }
      if (
        (e.code === 'minimum-error' || e.code === 'maximum-error') &&
        typeof e.data.schema?.minimum === 'number' &&
        typeof e.data.schema?.maximum === 'number' &&
        e.data.schema.minimum === e.data.schema.maximum
      ) {
        return {
          ...e,
          message: t('app.forms.validationErrors.minMaxNumberError', {
            expected: formatNumberValidationBound(
              e.data.schema.minimum,
              e.data.schema.options ?? {}
            ),
          }),
        };
      }
      if (e.code === 'maximum-error' && typeof e.data.maximum === 'number') {
        return {
          ...e,
          message: t('app.forms.validationErrors.maximumError', {
            expected: formatNumberValidationBound(
              e.data.maximum,
              e.data.schema?.options ?? {}
            ),
          }),
        };
      }
      if (e.code === 'minimum-error' && typeof e.data.minimum === 'number') {
        return {
          ...e,
          message: t('app.forms.validationErrors.minimumError', {
            expected: formatNumberValidationBound(
              e.data.minimum,
              e.data.schema?.options ?? {}
            ),
          }),
        };
      }
      if (e.code === 'pattern-error') {
        return {
          ...e,
          message: t('app.forms.validationErrors.patternError', {
            pattern: String(e.data.description ?? e.data.pattern ?? ''),
          }),
        };
      }
      if (e.code === 'custom-min-date-error') {
        return {
          ...e,
          message: t('app.forms.validationErrors.minDateError', {
            date: formatDateValidationBound(
              e.compareValue,
              e.data.schema?.options ?? {}
            ),
          }),
        };
      }
      if (e.code === 'custom-max-date-error') {
        return {
          ...e,
          message: t('app.forms.validationErrors.maxDateError', {
            date: formatDateValidationBound(
              e.compareValue,
              e.data.schema?.options ?? {}
            ),
          }),
        };
      }
      if (e.code === 'custom-should-be-max-date-error') {
        return {
          ...e,
          message: t('app.forms.validationErrors.shouldBeMaxError'),
        };
      }
      return e;
    });
  }, [t, errors]);
}

export const getDateFromString = (dateString: string): Date | undefined => {
  const directDate = new Date(dateString);
  if (isDateValid(directDate)) {
    return directDate;
  }
  return undefined;
};

export const getDateFromJsonRef = (
  stringRef: string,
  rootNode: Node,
  dateNode: StringNode
): Date | undefined => {
  const referencedNode = getNodeFromJsonPath(
    dateNode.pointer,
    stringRef,
    rootNode
  );
  if (
    referencedNode &&
    isStringNode(referencedNode) &&
    referencedNode.schema.format === 'date' &&
    referencedNode.value
  ) {
    return getDateFromString(referencedNode.value);
  }
  return undefined;
};
